import { EventBus } from "@xbs/lib-state";
const cantSendYet = Symbol();
export default class RestDataProvider extends EventBus {
    constructor(url) {
        super();
        this._customHeaders = {};
        this._url = url;
        this._queue = new ActionQueue();
        const defaultHandlers = {
            "add-card": {
                ignoreID: true,
                handler: (ev) => {
                    ev.card = this.prepareCard(ev.card);
                    return this.send("cards", "POST", ev);
                },
            },
            "update-card": {
                debounce: 500,
                handler: (ev) => {
                    ev.card = this.prepareCard(ev.card);
                    return this.send(`cards/${ev.id}`, "PUT", ev);
                },
            },
            "move-card": {
                handler: (ev) => this.send(`cards/${ev.id}/move`, "PUT", ev),
            },
            "delete-card": {
                handler: (ev) => this.send(`cards/${ev.id}`, "DELETE"),
            },
            "add-column": {
                ignoreID: true,
                handler: (ev) => this.send(`columns`, "POST", ev),
            },
            "update-column": {
                debounce: 500,
                handler: (ev) => this.send(`columns/${ev.id}`, "PUT", ev),
            },
            "move-column": {
                handler: (ev) => this.send(`columns/${ev.id}/move`, "PUT", ev),
            },
            "delete-column": {
                handler: (ev) => this.send(`columns/${ev.id}`, "DELETE"),
            },
            "add-row": {
                ignoreID: true,
                handler: (ev) => this.send(`rows`, "POST", ev),
            },
            "update-row": {
                debounce: 500,
                handler: (ev) => this.send(`rows/${ev.id}`, "PUT", ev),
            },
            "move-row": {
                handler: (ev) => this.send(`rows/${ev.id}/move`, "PUT", ev),
            },
            "delete-row": {
                handler: (ev) => this.send(`rows/${ev.id}`, "DELETE"),
            },
        };
        const handlers = this.getHandlers(defaultHandlers);
        for (const x in handlers) {
            this.on(x, ev => {
                if (!ev.skipProvider)
                    this._queue.add(x, ev, handlers[x]);
            });
        }
    }
    getCards() {
        return this.send("cards", "GET").then(this.parseCards);
    }
    getColumns() {
        return this.send("columns", "GET");
    }
    getRows() {
        return this.send("rows", "GET");
    }
    getUsers() {
        return this.send("users", "GET");
    }
    getHandlers(handlers) {
        return handlers;
    }
    setHeaders(headers) {
        this._customHeaders = headers;
    }
    getIDResolver() {
        return this._queue.resolve.bind(this._queue);
    }
    send(url, method, data, customHeaders = {}) {
        const headers = {
            "Content-Type": "application/json",
            ...customHeaders,
            ...this._customHeaders,
        };
        const req = {
            method,
            headers,
        };
        if (data) {
            req.body = typeof data === "object" ? JSON.stringify(data) : data;
        }
        return fetch(`${this._url}/${url}`, req).then(res => res.json());
    }
    parseCards(data) {
        data.forEach(card => {
            if (card.end_date) {
                card.end_date = new Date(card.end_date);
            }
            if (card.start_date) {
                card.start_date = new Date(card.start_date);
            }
            return card;
        });
        return data;
    }
    prepareCard(card) {
        if (!card)
            return null;
        return {
            ...card,
            users: card.users || null,
        };
    }
}
class ActionQueue {
    constructor() {
        this._awaitAddingQueue = [];
        this._queue = {};
        this._idPool = {};
        this._backId = [];
        this.add = this.add.bind(this);
    }
    resolve(id, type) {
        const col = this._backId[type];
        if (typeof col === "undefined")
            return id;
        const tID = col[id];
        return typeof tID === "undefined" ? id : tID;
    }
    add(action, data, proc) {
        if (proc.debounce) {
            const qid = `${action}"/"${data.id}`;
            const item = this._queue[qid];
            if (item) {
                clearTimeout(item);
            }
            this._queue[qid] = setTimeout(() => {
                this.add(action, data, { ...proc, debounce: false });
            }, proc.debounce);
            return;
        }
        const ready = this.tryExec(action, data, proc);
        if (ready === null) {
            //can't send data yet
            this._awaitAddingQueue.push({ action, data, proc });
        }
        else {
            ready.then((res) => {
                if (res &&
                    res.id &&
                    res.id != data.id &&
                    this.isTempID(data.id)) {
                    this._idPool[data.id] = res.id;
                    if (proc.kind) {
                        let t = this._backId[proc.kind];
                        if (!t)
                            t = this._backId[proc.kind] = {};
                        t[res.id] = data.id;
                    }
                    this.execQueue();
                }
            });
        }
    }
    tryExec(action, data, proc) {
        const ready = this.exec(action, data, proc);
        if (ready === null) {
            this._awaitAddingQueue.push({ action, data, proc });
        }
        return ready;
    }
    exec(action, data, proc) {
        const correctData = this.correctID(data, proc.ignoreID ? data.id : null);
        if (correctData === cantSendYet) {
            return null;
        }
        return proc.handler(correctData, action, data);
    }
    isTempID(v) {
        return (typeof v === "string" &&
            v.length === 20 &&
            parseInt(v.substr(7)) > 1e12); // new Date(2000,0)
    }
    correctID(obj, ignore) {
        let copy = null;
        for (const key in obj) {
            const test = obj[key];
            if (typeof test === "object") {
                const after = this.correctID(test, ignore);
                if (after !== test) {
                    if (after === cantSendYet) {
                        return cantSendYet;
                    }
                    if (copy === null) {
                        copy = { ...obj };
                    }
                    copy[key] = after;
                }
            }
            else if (test !== ignore && this.isTempID(test)) {
                const hasRealID = this._idPool[test];
                if (!hasRealID) {
                    return cantSendYet;
                }
                if (copy === null) {
                    copy = { ...obj };
                }
                copy[key] = hasRealID;
            }
        }
        return copy || obj;
    }
    execQueue() {
        this._awaitAddingQueue = this._awaitAddingQueue
            .map(a => {
            return this.tryExec(a.action, a.data, a.proc)
                ? null
                : a;
        })
            .filter(a => a !== null);
    }
}
//# sourceMappingURL=RestDataProvider.js.map